import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { MapPin, AlertCircle, CheckCircle } from 'lucide-react';

interface FieldMappingEditorProps {
  sourceFields: string[];
  targetFields: Array<{ name: string; required: boolean; description?: string }>;
  initialMapping?: Record<string, string>;
  onChange: (mapping: Record<string, string>) => void;
}

const FieldMappingEditor: React.FC<FieldMappingEditorProps> = ({
  sourceFields,
  targetFields,
  initialMapping = {},
  onChange,
}) => {
  const [mapping, setMapping] = useState<Record<string, string>>(initialMapping);
  const [sourceItems, setSourceItems] = useState(
    sourceFields.map((field, index) => ({
      id: `source-${index}`,
      content: field,
      mapped: !!Object.values(initialMapping).includes(field),
    }))
  );
  const [targetItems, setTargetItems] = useState(
    targetFields.map((field, index) => ({
      id: `target-${index}`,
      name: field.name,
      required: field.required,
      description: field.description,
      mapped: !!initialMapping[field.name],
    }))
  );

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    if (source.droppableId === 'source' && destination.droppableId === 'target') {
      // Map source field to target
      const sourceField = sourceItems.find(item => item.id === draggableId)?.content;
      const targetField = targetItems[destination.index].name;

      if (sourceField && targetField) {
        const newMapping = { ...mapping, [targetField]: sourceField };
        setMapping(newMapping);
        onChange(newMapping);

        // Update mapped status
        setSourceItems(prev =>
          prev.map(item =>
            item.content === sourceField ? { ...item, mapped: true } : item
          )
        );
        setTargetItems(prev =>
          prev.map(item =>
            item.name === targetField ? { ...item, mapped: true } : item
          )
        );
      }
    } else if (source.droppableId === 'target' && destination.droppableId === 'source') {
      // Unmap field
      const targetField = targetItems.find(item => item.id === draggableId)?.name;
      if (targetField && mapping[targetField]) {
        const newMapping = { ...mapping };
        const sourceField = newMapping[targetField];
        delete newMapping[targetField];
        setMapping(newMapping);
        onChange(newMapping);

        // Update mapped status
        setSourceItems(prev =>
          prev.map(item =>
            item.content === sourceField ? { ...item, mapped: false } : item
          )
        );
        setTargetItems(prev =>
          prev.map(item =>
            item.name === targetField ? { ...item, mapped: false } : item
          )
        );
      }
    }
  };

  const clearMapping = (targetField: string) => {
    const newMapping = { ...mapping };
    const sourceField = newMapping[targetField];
    delete newMapping[targetField];
    setMapping(newMapping);
    onChange(newMapping);

    // Update mapped status
    setSourceItems(prev =>
      prev.map(item =>
        item.content === sourceField ? { ...item, mapped: false } : item
      )
    );
    setTargetItems(prev =>
      prev.map(item =>
        item.name === targetField ? { ...item, mapped: false } : item
      )
    );
  };

  const getValidationStatus = () => {
    const requiredFields = targetFields.filter(f => f.required);
    const mappedRequired = requiredFields.filter(f => mapping[f.name]);
    const hasAllRequired = mappedRequired.length === requiredFields.length;

    if (hasAllRequired) {
      return { status: 'valid', message: 'All required fields are mapped' };
    } else {
      const missing = requiredFields.filter(f => !mapping[f.name]).map(f => f.name);
      return {
        status: 'invalid',
        message: `Missing required fields: ${missing.join(', ')}`,
      };
    }
  };

  const validation = getValidationStatus();

  return (
    <div className="space-y-4">
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-2 gap-8">
          {/* Source Fields */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Source Fields</h3>
            <Droppable droppableId="source">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`space-y-2 min-h-[200px] p-4 border-2 border-dashed rounded-lg ${
                    snapshot.isDraggingOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
                  }`}
                >
                  {sourceItems.map((item, index) => (
                    <Draggable key={item.id} draggableId={item.id} index={index} isDragDisabled={item.mapped}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`p-3 bg-white border rounded-lg shadow-sm transition-colors ${
                            snapshot.isDragging ? 'shadow-lg border-blue-400' : 'border-gray-200'
                          } ${item.mapped ? 'opacity-50 cursor-not-allowed' : 'cursor-move hover:border-gray-300'}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">{item.content}</span>
                            {item.mapped && <CheckCircle className="h-4 w-4 text-green-500" />}
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>

          {/* Target Fields */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Target Fields</h3>
            <Droppable droppableId="target">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`space-y-2 min-h-[200px] p-4 border-2 border-dashed rounded-lg ${
                    snapshot.isDraggingOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
                  }`}
                >
                  {targetFields.map((field, index) => {
                    const isMapped = !!mapping[field.name];
                    return (
                    <Draggable key={`target-${index}`} draggableId={`target-${index}`} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`p-3 bg-white border rounded-lg shadow-sm transition-colors ${
                            snapshot.isDragging ? 'shadow-lg border-blue-400' : 'border-gray-200'
                          } ${isMapped ? 'border-green-200 bg-green-50' : 'hover:border-gray-300'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium">{field.name}</span>
                                {field.required && <span className="text-red-500 text-xs">*</span>}
                                {isMapped && <MapPin className="h-4 w-4 text-green-500" />}
                              </div>
                              {field.description && (
                                <p className="text-xs text-gray-500 mt-1">{field.description}</p>
                              )}
                              {mapping[field.name] && (
                                <p className="text-xs text-blue-600 mt-1">
                                  ← {mapping[field.name]}
                                </p>
                              )}
                            </div>
                            {isMapped && (
                              <button
                                onClick={() => clearMapping(field.name)}
                                className="ml-2 p-1 text-gray-400 hover:text-red-500 transition-colors"
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </div>
      </DragDropContext>

      {/* Validation Status */}
      <div className={`p-4 rounded-lg border ${
        validation.status === 'valid'
          ? 'bg-green-50 border-green-200 text-green-800'
          : 'bg-yellow-50 border-yellow-200 text-yellow-800'
      }`}>
        <div className="flex items-center space-x-2">
          {validation.status === 'valid' ? (
            <CheckCircle className="h-5 w-5" />
          ) : (
            <AlertCircle className="h-5 w-5" />
          )}
          <span className="text-sm font-medium">{validation.message}</span>
        </div>
      </div>

      {/* Instructions */}
      <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
        <p className="font-medium mb-2">How to map fields:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Drag source fields from the left to target fields on the right</li>
          <li>Required fields are marked with a red asterisk (*)</li>
          <li>Drag mapped fields back to the left to unmap them</li>
          <li>Click the × button to clear individual mappings</li>
        </ul>
      </div>
    </div>
  );
};

export default FieldMappingEditor;
